import { trackEventsRequest } from 'APIs/clickstreamAPI/events';
import { isEventAllowedForAmplitude } from 'utils/analytics/amplitudeAllowlist';
import { formatAmplitudeProps } from 'utils/analytics/formatAmplitudeProps';
import * as amplitudeSetup from 'utils/analytics/setupAmplitude';
import { isBetaDomain, isProdEnvironment } from 'utils/environment';
import { getUserData } from 'utils/functions/getUserData';
import dayjs from 'utils/setup/setupDayjs';
import { BaseEventData, EventData, EventName } from './types';

const isProdEnv = isProdEnvironment();

interface UseTrackEventReturn {
  trackEvent(eventName: EventName, payload?: EventData): void;
}

function replaceNullishValues(key, value) {
  // Converting null and NaN to undefined, which will be later removed from the payload by JSON.stringify()
  if (value === null || Number.isNaN(value)) {
    return undefined;
  }

  return value;
}

function useTrackEvent(): UseTrackEventReturn {
  const { trigger: triggerTrackEvent } = trackEventsRequest();

  function getEventPayload(eventName: EventName, payload?: EventData) {
    switch (eventName) {
      case 'teacher_login':
      case 'click_on_test_type_tab':
      case 'test_page_access':
      case 'pre_writing_themes_access':
      case 'pre-writing_material_tab_click':
      case 'students_page_views':
      case 'reset_password_click':
      case 'update_student_profile_click':
      case 'themes_page_access':
      case 'correction_grids_page_access':
      case 'evolution_report_page_access':
      case 'knowledge_center_page_access':
      case 'sidebar_click_learning_path':
      case 'scheduled_card_click':
      case 'in_progress_card_click':
      case 'finished_card_click':
      case 'baixo_engajamento_click':
      case 'bom_desempenho_click':
      case 'com_dificuldades_click':
      case 'learning_path_quick_access_click':
      case 'create_learning_path_click':
      case 'redirect_to_evolution_report_click':
      case 'reminder_click':
      case 'redirect_to_support_material_click':
      case 'redirect_to_learning_path_report_click':
      case 'redirect_to_learning_path_report':
      case 'redirect_to_rewrite_report':
      case 'learning_path_list_page_access':
      case 'learning_path_report_bncc_skills_click':
      case 'learning_path_report_theme_click':
      case 'learning_path_report_questionnaire_content_click':
      case 'learning_path_report_indicators_click':
      case 'write_support_material_click':
      case 'questionnaire_support_material_click':
      case 'learning_path_report_write_tab_click':
      case 'learning_path_report_questionnaire_tab_click':
      case 'learning_path_report_rewrite_tab_click':
      case 'learning_path_report_low_performance_write_click':
      case 'learning_path_report_low_performance_questionnaire_click':
        return {};

      case 'learning_path_card_expanded':
      case 'student_support_material_click':
      case 'teacher_support_material_click':
        return {
          order: payload?.order,
          page: payload?.page,
        };

      case 'home_modal_filters':
      case 'learning_path_list_page_filters':
      case 'learning_path_report_student_performance_filters':
        return {
          filters: payload?.filters,
          order: payload?.order,
          page: payload?.page,
        };

      case 'home_page_access':
        return { ...payload?.user };

      case 'enem_test_access':
        return { ...payload?.test };

      case 'reviewed_composition_access':
        return { ...payload?.test, ...payload?.composition };

      case 'multigenre_reading_button_click':
      case 'reading_indicators_report_next_slide_click':
      case 'reading_indicators_report_previous_slide_click':
      case 'reopen_test_button_click':
      case 'reading_indicators_question_click':
      case 'learning_path_report_access':
      case 'learning_path_report_step_click':
      case 'learning_path_access_student_list_button_click':
      case 'learning_path_access_engagement_button_click':
      case 'learning_path_BNCC_skills_button_click':
      case 'learning_path_grammatical_aspects_button_click':
      case 'learning_path_pre_writing_button_click':
      case 'grade_and_proficiency_level_card_click':
      case 'cognitive_axes_saeb_card_click':
      case 'student_engagement_card_click':
      case 'teachers_material_card_click':
      case 'check_questionnaire_card_click':
      case 'repertoire_material_card_click':
      case 'learning_path_report_open_steps_modal_click':
        return { ...payload?.learningPathInstance, ...payload?.school };

      case 'composition_review_edited':
        return { ...payload?.review };

      case 'learning_path_in_progress_composition_access':
        return { ...payload?.learningPathInstance, ...payload?.composition };

      case 'clicked_on_recommendation_alert_teacher':
      case 'accessed_tech_recommendations_page_teacher':
      case 'accessed_profiles_page_teacher':
        return {
          ...payload?.user,
          ...payload?.network,
          ...payload?.school,
        };

      case 'clicked_on_recommendation_video_teacher':
      case 'sent_recommendation_teacher':
        return {
          ...payload?.recommendation,
          ...payload?.user,
          ...payload?.network,
          ...payload?.school,
        };

      case 'learning_path_report_composition_details_click':
        return { ...payload?.student };

      default:
        return {};
    }
  }

  function trackEvent(eventName: EventName, payload?: EventData) {
    const { user } = getUserData();

    const baseEventPayload: BaseEventData = {
      event_name: eventName,
      user_id: user?.id || 0,
      event_date: dayjs().format('YYYYMMDD'),
      event_time: dayjs().format('HHmmss'),
      user_system_info: navigator.userAgent,
      user_screen_resolution: `${window.screen.height}x${window.screen.width}`,
      pathname: window.location.pathname,
      version: window.location.pathname.includes('v2') ? 2 : 1,
    };

    const requestPayload = JSON.stringify(
      {
        ...baseEventPayload,
        ...getEventPayload(eventName, payload),
      },
      replaceNullishValues
    );

    if (!isProdEnv && !isBetaDomain && !import.meta.env.VITEST) {
      console.log(`Track Event: ${eventName}`, JSON.parse(requestPayload));
    }

    if (isProdEnv && !isBetaDomain) {
      const eventData = {
        DeliveryStreamName: isProdEnv
          ? 'clickstream-prod-stream'
          : 'clickstream-dev-stream',
        Record: {
          Data: window.btoa(requestPayload),
        },
      };

      triggerTrackEvent(eventData);
    }

    // Mirror to Amplitude if ready and event is allowlisted and user present and events are enabled.
    if (
      amplitudeSetup.isAmplitudeReady() &&
      user?.id &&
      isEventAllowedForAmplitude(eventName) &&
      isProdEnv &&
      !isBetaDomain
    ) {
      const props = formatAmplitudeProps(eventName, payload);

      amplitudeSetup.trackAmplitudeEvent(eventName, props);
    }
  }

  return {
    trackEvent,
  };
}

export default useTrackEvent;
